/* import __COLOCATED_TEMPLATE__ from './conversation.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { trackedRef } from 'ember-ref-bucket';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import { scrollToBottom, isScrolledToBottom } from 'embercom/helpers/scrolling-helpers';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type { FinVoiceSession } from 'embercom/objects/ai-agent/fin-voice/fin-voice-session';
import type { FormattedConversationItem } from './conversation-item';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type IntlService from 'ember-intl/services/intl';

export interface ConversationArgs {
  isLoading: boolean;
  sessions: FinVoiceSession[];
  selectedNumberConfigurationPhoneNumberId: string | undefined;
  selectedNumberConfigurationPhoneNumber: string | undefined;
  hasDevFeatures: boolean;
  verboseLogsEnabled: boolean;
  isDocked?: boolean;
  selectConfig?: (phoneNumber: string | undefined) => void;
  connect: () => Promise<void>;
}

interface Signature {
  Args: ConversationArgs;
  Blocks: {
    controls: [];
  };
  Element: HTMLElement;
}

type FormattedSession = Pick<FinVoiceSession, 'sessionError' | 'sessionId'> & {
  sessionStartTimestamp: string;
  sessionEndTimestamp: string | null;
  conversation: FormattedConversationItem[];
};

export default class Conversation extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked private _userHasScrolled = false;
  @tracked hasNewMessage = false;
  @tracked selectedConfig: FinVoiceNumberConfiguration | undefined = undefined;

  @trackedRef('containerElement')
  private _containerElement?: HTMLElement;

  constructor(owner: unknown, args: ConversationArgs) {
    super(owner, args);
  }

  sessionsWatcher = DeduplicatedAsyncData(
    this,
    () => [this.args.sessions],
    (sessions) => {
      if (!this._userHasScrolled) {
        if (this._containerElement) {
          requestAnimationFrame(() => {
            scrollToBottom(this._containerElement!);
          });
        }

        this.hasNewMessage = false;
      } else {
        this.hasNewMessage = true;
      }
      return sessions;
    },
  );

  get formattedSessions(): FormattedSession[] {
    return (this.sessionsWatcher.value || []).map(
      ({ sessionStartTimestamp, sessionEndTimestamp, sessionError, sessionId, conversation }) => {
        let formattedConversation: FormattedConversationItem[] = [];

        conversation.messages.forEach((message) => {
          switch (message.type) {
            case 'function_call': {
              let shouldAddItem = message.status === 'loading' || this.args.verboseLogsEnabled;

              if (!shouldAddItem) {
                return;
              }

              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                toolName: message.toolName,
                toolCallId: message.toolCallId,
                toolArguments: message.toolArguments,
                toolOutput: message.toolOutput,
                status: message.status,
                itemId: message.itemId,
                type: message.type,
              });
              break;
            }
            case 'start': {
              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                vadSettings: JSON.stringify(message.vadSettings),
                voice: message.voice,
                streamId: message.streamId,
                pipeline: message.pipeline,
              });
              break;
            }
            case 'call_outcome': {
              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                outcome: message.outcome,
                reason: message.reason,
                conversationId: message.conversationId,
                adminId: message.adminId,
                conversationSummary: message.conversationSummary,
                customDataAttributes: message.customDataAttributes,
                intentClassification: message.intentClassification,
              });
              break;
            }
            case 'escalate_to_human': {
              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                escalationContext: message.escalationContext,
                customDataAttributes: message.customDataAttributes,
                intentClassification: message.intentClassification,
              });
              break;
            }
            case 'search': {
              if (message.status !== 'completed') {
                return;
              }

              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                searchId: message.searchId,
                query: message.query,
                articleSources: message.articleSources,
                triggeredGuidance: message.triggeredGuidance,
              });
              break;
            }
            case 'data_connector_call': {
              let shouldAddItem = message.status === 'loading' || this.args.verboseLogsEnabled;

              if (!shouldAddItem) {
                return;
              }

              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                actionName: message.actionName,
                actionId: message.actionId,
                requestParams: message.requestParams,
                resultStatus: message.resultStatus,
                resultError: message.resultError,
                resultUpdates: message.resultUpdates,
                durationMs: message.durationMs,
              });
              break;
            }
            case 'error': {
              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                error: message.error,
                errorType: message.errorType,
                instructionIndex: message.instructionIndex,
                instructionType: message.instructionType,
                instructionLabel: message.instructionLabel,
              });
              break;
            }
            case 'code_evaluation_error': {
              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                instructionIndex: message.instructionIndex,
                instructionType: message.instructionType,
                fieldName: message.fieldName,
                codeExpression: message.codeExpression,
                inputsSnapshot: message.inputsSnapshot,
                error: message.error,
                suggestion: message.suggestion,
              });
              break;
            }
            case 'guidance_triggered': {
              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                match_type: message.match_type,
                rule_text: message.rule_text,
                rule_id: message.rule_id,
                userIntent: message.userIntent,
                title: message.title,
                guideline_id: message.guideline_id,
              });
              break;
            }
            case 'context_updated': {
              if (!this.args.verboseLogsEnabled) {
                return;
              }

              formattedConversation.push({
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                status: message.status,
                itemId: message.itemId,
                type: message.type,
                source: message.source,
                updates: message.updates,
              });
              break;
            }
            default:
              formattedConversation.push({
                participant: message.role === 'user' ? 'user' : 'fin',
                timestamp: new Date(message.timestamp).toLocaleTimeString(),
                transcript: message.transcript,
                status: message.status,
                itemId: message.itemId,
                errorCode: message.errorCode,
                type: message.type,
              });
              break;
          }
        });

        return {
          sessionId,
          sessionStartTimestamp: new Date(sessionStartTimestamp).toLocaleTimeString(),
          sessionEndTimestamp: sessionEndTimestamp
            ? new Date(sessionEndTimestamp).toLocaleTimeString()
            : null,
          sessionError,
          conversation: formattedConversation,
        };
      },
    );
  }

  get hasSessions() {
    return !!this.formattedSessions.length;
  }

  get controlsContainerStyling() {
    if (this.args.isDocked) {
      return 'w-full';
    }

    return 'absolute bottom-0 right-0 left-0 mb-8 mx-auto w-fit-content';
  }

  get newMessageButtonPositionStyling() {
    if (this.args.isDocked) {
      if (this.args.hasDevFeatures) {
        return 'bottom-16 mb-20';
      }

      return 'bottom-16 mb-8';
    }

    return 'bottom-0 mb-8';
  }

  @action
  handleScroll() {
    let isAtBottom = isScrolledToBottom(this._containerElement);
    this._userHasScrolled = !isAtBottom;

    if (isAtBottom) {
      this.hasNewMessage = false;
    }
  }

  @action
  jumpToBottom() {
    if (this._containerElement) {
      scrollToBottom(this._containerElement);
      this.hasNewMessage = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::Conversation': typeof Conversation;
  }
}
